import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Card, Layout, Typography, Alert, Space } from 'antd';
// import 'antd/dist/reset.css'; // Import Ant Design CSS

const { Content } = Layout;
const { Title } = Typography;

const TestMic = () => {
    // --- State Variables ---
    const [isRecording, setIsRecording] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null); // Lưu URL của Blob âm thanh
    const [errorMessage, setErrorMessage] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false); // Trạng thái xử lý khi dừng ghi

    // --- Refs for DOM elements and Web APIs ---
    const canvasRef = useRef(null); // Ref cho thẻ canvas
    const audioPlayerRef = useRef(null); // Ref cho thẻ audio
    const mediaRecorderRef = useRef(null); // Ref cho đối tượng MediaRecorder
    const audioChunksRef = useRef([]); // Ref lưu các chunk audio
    const audioContextRef = useRef(null); // Ref cho AudioContext
    const analyserRef = useRef(null); // Ref cho AnalyserNode
    const sourceNodeRef = useRef(null); // Ref cho nguồn âm thanh từ mic
    const playbackSourceNodeRef = useRef(null); // Ref cho nguồn âm thanh từ audio element
    const dataArrayRef = useRef(null); // Ref cho mảng dữ liệu tần số
    const rafIdRef = useRef(null); // Ref lưu ID của requestAnimationFrame
    const streamRef = useRef(null); // Ref lưu stream từ microphone

    useEffect(() => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setErrorMessage('API MediaDevices không được hỗ trợ trên trình duyệt này.');
            return;
        }
        setErrorMessage('API MediaDevices được hỗ trợ trên trình duyệt này.');
        requestMicrophonePermission();
        },[]);
        const requestMicrophonePermission = async () => {
            try {
              const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
              console.log("Microphone permission granted");
              alert("Microphone is allowed!");
              
            } catch (error) {
              
              alert("Microphone permission denied");
            }
          };
    // --- Helper Functions ---

    // Hàm khởi tạo AudioContext và Analyser
    const setupAudioProcessing = useCallback(() => {
        // Chỉ khởi tạo nếu chưa có hoặc đã bị đóng
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
            try {
                // Đảm bảo resume context nếu nó bị suspended (chính sách autoplay)
                if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                    audioContextRef.current.resume();
                } else {
                    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                }
                analyserRef.current = audioContextRef.current.createAnalyser();
                analyserRef.current.fftSize = 2048;
                const bufferLength = analyserRef.current.frequencyBinCount;
                dataArrayRef.current = new Uint8Array(bufferLength);
                console.log("AudioContext and Analyser setup complete.");
                return true; // Thành công
            } catch (e) {
                setErrorMessage('Trình duyệt của bạn không hỗ trợ Web Audio API.');
                console.error('Lỗi khởi tạo AudioContext:', e);
                return false; // Thất bại
            }
        }
        return true; // Đã có sẵn
    }, []);

    // Hàm dừng vẽ waveform
    const stopDrawing = useCallback(() => {
        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
        const canvas = canvasRef.current;
        if (canvas) {
            const canvasCtx = canvas.getContext('2d');
            // Xóa canvas khi dừng
            canvasCtx.fillStyle = '#f3f4f6'; // Màu nền giống container
            canvasCtx.fillRect(0, 0, canvas.width, canvas.height);
        }
        console.log("Drawing stopped.");
    }, []);

    // Hàm vẽ waveform
    const drawWaveform = useCallback(() => {
        const canvas = canvasRef.current;
        const analyser = analyserRef.current;
        const dataArray = dataArrayRef.current;

        if (!canvas || !analyser || !dataArray || !audioContextRef.current || audioContextRef.current.state !== 'running') {
            // Nếu context chưa chạy hoặc thiếu phần tử -> dừng vẽ
             if (audioContextRef.current && audioContextRef.current.state !== 'running') {
                console.warn("AudioContext not running, stopping draw loop.");
             }
            stopDrawing();
            return;
        }

        // Yêu cầu frame tiếp theo
        rafIdRef.current = requestAnimationFrame(drawWaveform);

        const canvasCtx = canvas.getContext('2d');
        const bufferLength = analyser.frequencyBinCount;

        // Lấy dữ liệu tần số
        analyser.getByteFrequencyData(dataArray);

        // Xóa canvas
        canvasCtx.fillStyle = '#f3f4f6';
        canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

        const barWidth = (canvas.width / bufferLength) * 2.5;
        let barHeight;
        let x = 0;

        // Vẽ các thanh
        for (let i = 0; i < bufferLength; i++) {
            barHeight = dataArray[i] * (canvas.height / 255); // Chiều cao tỉ lệ

            // Màu cầu vồng
            const hue = (i / bufferLength) * 360;
            canvasCtx.fillStyle = `hsl(${hue}, 100%, 50%)`;

            canvasCtx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);

            x += barWidth + 1; // Di chuyển x
        }
    }, [stopDrawing]); // Phụ thuộc vào stopDrawing để có thể gọi nó

    // Hàm xử lý resize canvas
    const handleResize = useCallback(() => {
        const canvas = canvasRef.current;
        if (canvas && canvas.offsetParent) { // Kiểm tra canvas có hiển thị không
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
            // Nếu đang vẽ thì vẽ lại frame hiện tại, nếu không thì để trống
            if (!rafIdRef.current) {
                 const canvasCtx = canvas.getContext('2d');
                 canvasCtx.fillStyle = '#f3f4f6';
                 canvasCtx.fillRect(0, 0, canvas.width, canvas.height);
            }
             console.log("Canvas resized:", canvas.width, canvas.height);
        }
    }, []); // Không cần phụ thuộc draw/stop vì nó chỉ set kích thước


    // --- Event Handlers ---

    const handleRecord = useCallback(async () => {
        setErrorMessage(null); // Xóa lỗi cũ
        setAudioUrl(null); // Xóa bản ghi cũ khi bắt đầu ghi mới
         if (audioPlayerRef.current) {
             audioPlayerRef.current.src = ''; // Gỡ src khỏi audio element
         }

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setErrorMessage('API MediaDevices không được hỗ trợ trên trình duyệt này.');
            return;
        }

        // Khởi tạo AudioContext và Analyser
        if (!setupAudioProcessing()) {
            return; // Dừng nếu không khởi tạo được audio context
        }

         // Đảm bảo resume context (quan trọng khi bắt đầu bằng user action)
        if (audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
             console.log("AudioContext resumed.");
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream; // Lưu lại stream để dừng track sau
            setIsRecording(true);
            audioChunksRef.current = []; // Reset chunks

            // Ngắt kết nối nguồn cũ (nếu có)
            if (sourceNodeRef.current) {
                sourceNodeRef.current.disconnect();
                sourceNodeRef.current = null;
                 console.log("Previous mic source disconnected.");
            }
            if (playbackSourceNodeRef.current) {
                playbackSourceNodeRef.current.disconnect();
                playbackSourceNodeRef.current = null;
                 console.log("Previous playback source disconnected.");
            }


             // Tạo nguồn từ stream micro và kết nối vào analyser
            sourceNodeRef.current = audioContextRef.current.createMediaStreamSource(stream);
            sourceNodeRef.current.connect(analyserRef.current);
            // Không cần connect analyser đến destination khi chỉ visualize mic input
             console.log("Mic stream connected to analyser.");

            mediaRecorderRef.current = new MediaRecorder(stream);

            mediaRecorderRef.current.ondataavailable = event => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                console.log("Recording stopped. Processing audio...");
                setIsProcessing(true); // Bắt đầu xử lý

                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
                const url = URL.createObjectURL(audioBlob);
                setAudioUrl(url);
                console.log("Audio Blob created:", url);

                // Dừng các track của stream micro
                streamRef.current?.getTracks().forEach(track => track.stop());
                streamRef.current = null; // Xóa ref stream
                console.log("Microphone tracks stopped.");

                // Ngắt kết nối nguồn mic khỏi analyser
                if (sourceNodeRef.current) {
                    sourceNodeRef.current.disconnect();
                    sourceNodeRef.current = null;
                     console.log("Mic source disconnected after recording stop.");
                }

                setIsRecording(false);
                setIsProcessing(false); // Kết thúc xử lý
                stopDrawing(); // Dừng vẽ khi dừng ghi
            };

            mediaRecorderRef.current.onerror = (event) => {
                 console.error("MediaRecorder error:", event.error);
                 setErrorMessage(`Lỗi ghi âm: ${event.error.name} - ${event.error.message}`);
                 setIsRecording(false);
                 setIsProcessing(false);
                 stopDrawing();
                 streamRef.current?.getTracks().forEach(track => track.stop());
                 streamRef.current = null;
                  if (sourceNodeRef.current) {
                    sourceNodeRef.current.disconnect();
                    sourceNodeRef.current = null;
                 }
            };


            mediaRecorderRef.current.start();
            console.log("Recording started.");

            // Bắt đầu vẽ waveform
            handleResize(); // Đảm bảo kích thước canvas đúng trước khi vẽ
            drawWaveform();

        } catch (err) {
            console.error('Lỗi getUserMedia:', err);
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setErrorMessage('Bạn đã từ chối quyền truy cập micrô.');
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError'){
                 setErrorMessage('Không tìm thấy thiết bị micrô nào.');
            }
             else {
                setErrorMessage('Không thể truy cập micrô. Vui lòng kiểm tra quyền.');
            }
            setIsRecording(false);
        }
    }, [setupAudioProcessing, drawWaveform, stopDrawing, handleResize]); // Thêm handleResize vào dependencies

    const handleStop = useCallback(() => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            // Logic còn lại được xử lý trong onstop
            // Không gọi stopDrawing() ở đây vì onstop sẽ gọi
             console.log("Stop button clicked. Stopping recorder...");
        }
    }, [isRecording]);

    const handlePlay = useCallback(async () => {
        if (!audioUrl || !audioPlayerRef.current) {
            setErrorMessage("Chưa có bản ghi nào để phát hoặc audio element chưa sẵn sàng.");
            return;
        }
         if (isRecording) {
             setErrorMessage("Không thể phát lại khi đang ghi âm.");
             return;
         }

        setErrorMessage(null);

        // Khởi tạo AudioContext nếu cần
        if (!setupAudioProcessing()) {
             return;
         }
          // Đảm bảo resume context (quan trọng khi bắt đầu bằng user action)
        if (audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
             console.log("AudioContext resumed for playback.");
        }

        try {
             // Ngắt kết nối nguồn mic nếu còn
             if (sourceNodeRef.current) {
                 sourceNodeRef.current.disconnect();
                 sourceNodeRef.current = null;
                 console.log("Mic source disconnected before playback.");
             }
             // Ngắt kết nối nguồn playback cũ nếu đang phát lại trước đó
             if (playbackSourceNodeRef.current) {
                 playbackSourceNodeRef.current.disconnect();
                  console.log("Previous playback source disconnected.");
             }

            // Tạo nguồn từ audio element *chỉ khi chưa có*
            // hoặc nếu context đã bị đóng và được tạo lại
             if (!playbackSourceNodeRef.current || playbackSourceNodeRef.current.context !== audioContextRef.current ) {
                 playbackSourceNodeRef.current = audioContextRef.current.createMediaElementSource(audioPlayerRef.current);
                  console.log("MediaElementSource created for playback.");
             }


            // Kết nối nguồn phát lại -> analyser -> destination (loa)
            // Chỉ kết nối analyser -> destination MỘT LẦN hoặc khi context thay đổi
            try {
                playbackSourceNodeRef.current.connect(analyserRef.current);
                 console.log("Playback source connected to analyser.");
                 // Thử kết nối analyser với destination, bắt lỗi nếu đã kết nối
                analyserRef.current.connect(audioContextRef.current.destination);
                 console.log("Analyser connected to destination.");
            } catch(connectError) {
                 // Bỏ qua lỗi 'InvalidStateNode' nếu đã kết nối trước đó
                 if (connectError.name !== 'InvalidStateError') {
                    console.warn("Error connecting playback nodes (might already be connected):", connectError);
                 } else {
                     console.log("Analyser already connected to destination.");
                 }
            }


            // Bắt đầu phát audio
            await audioPlayerRef.current.play();
             console.log("Playback started.");
            // Bắt đầu vẽ waveform khi phát lại
            handleResize(); // Đảm bảo kích thước canvas
            drawWaveform();

        } catch (err) {
            console.error("Lỗi khi phát audio:", err);
            setErrorMessage(`Lỗi phát lại: ${err.message}`);
            stopDrawing(); // Dừng vẽ nếu không phát được
        }
    }, [audioUrl, isRecording, setupAudioProcessing, drawWaveform, stopDrawing, handleResize]);

     // Xử lý khi audio phát xong
     const handleAudioEnded = useCallback(() => {
         console.log("Playback ended.");
         stopDrawing();
         // Tùy chọn: Ngắt kết nối analyser khỏi loa để tiết kiệm tài nguyên
         // Tuy nhiên, việc này có thể gây ra click nhỏ khi bắt đầu phát lại lần sau
         // nếu không kết nối lại đúng cách. Xem xét kỹ lưỡng.
         // if (analyserRef.current && audioContextRef.current && audioContextRef.current.destination) {
         //     try {
         //         analyserRef.current.disconnect(audioContextRef.current.destination);
         //         console.log("Analyser disconnected from destination on end.");
         //     } catch (e) { console.warn("Error disconnecting analyser on end:", e); }
         // }
     }, [stopDrawing]);


    // --- Effects ---

    // Effect chạy 1 lần khi mount để gắn listener resize và cleanup khi unmount
    useEffect(() => {
        window.addEventListener('resize', handleResize);
         // Gọi lần đầu để set kích thước ban đầu
         handleResize();

        // Cleanup function
        return () => {
            console.log("Component unmounting. Cleaning up...");
            window.removeEventListener('resize', handleResize);

            // Dừng ghi âm nếu đang chạy
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
                mediaRecorderRef.current.stop();
            }
            // Dừng track micro
             streamRef.current?.getTracks().forEach(track => track.stop());

            // Hủy animation frame
            stopDrawing();

            // Ngắt kết nối và đóng AudioContext
            sourceNodeRef.current?.disconnect();
            playbackSourceNodeRef.current?.disconnect();
            analyserRef.current?.disconnect();
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close().then(() => console.log("AudioContext closed."));
            }


            // Thu hồi Object URL để giải phóng bộ nhớ
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
                 console.log("Object URL revoked:", audioUrl);
            }

             // Clear refs
             mediaRecorderRef.current = null;
             audioContextRef.current = null;
             analyserRef.current = null;
             sourceNodeRef.current = null;
             playbackSourceNodeRef.current = null;
             streamRef.current = null;
             audioChunksRef.current = [];
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Chạy 1 lần duy nhất khi mount

     // Effect để gắn/gỡ listener 'ended' cho audio element
     useEffect(() => {
         const audioElement = audioPlayerRef.current;
         if (audioElement) {
             audioElement.addEventListener('ended', handleAudioEnded);
             console.log("Added 'ended' event listener to audio player.");

             // Cleanup listener khi component unmount hoặc audioUrl thay đổi
             return () => {
                 audioElement.removeEventListener('ended', handleAudioEnded);
                  console.log("Removed 'ended' event listener from audio player.");
             };
         }
     }, [handleAudioEnded]); // Gắn lại listener nếu hàm handleAudioEnded thay đổi


    // Thu hồi URL cũ khi URL mới được tạo hoặc component unmount
    useEffect(() => {
        // Lưu URL hiện tại vào một biến tạm để cleanup function có thể truy cập
        const currentAudioUrl = audioUrl;
        return () => {
            if (currentAudioUrl) {
                URL.revokeObjectURL(currentAudioUrl);
                 console.log("Cleaned up (revoked) previous Object URL:", currentAudioUrl);
            }
        };
    }, [audioUrl]); // Chạy mỗi khi audioUrl thay đổi


    // --- JSX Structure ---
    return (
        <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', padding: '20px' }}>
            <Content style={{ maxWidth: '700px', width: '100%' }}>
                <Card bordered={false} style={{ boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
                    <Title level={2} style={{ textAlign: 'center', marginBottom: '24px' }}>
                        Kiểm tra Microphone
                    </Title>

                    {/* Container cho Canvas */}
                    <div style={{ height: '200px', backgroundColor: '#f3f4f6', marginBottom: '24px', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
                        <canvas
                            ref={canvasRef}
                            style={{ display: 'block', width: '100%', height: '100%' }}
                            // Đặt kích thước mặc định nhỏ để tránh lỗi khi ref chưa sẵn sàng
                            width="300"
                            height="150"
                        ></canvas>
                    </div>

                    {/* Hiển thị lỗi */}
                    {errorMessage && (
                        <>
                        <div style={{ color: 'red', marginBottom: '16px' }}>{errorMessage}</div>
                        {/* <Alert
                            message={errorMessage}
                            type="error"
                            showIcon
                            style={{ marginBottom: '16px' }}
                            closable // Cho phép đóng thông báo lỗi
                            onClose={() => setErrorMessage(null)}
                        /> */}
                        </>
                    )}

                    {/* Các nút điều khiển */}
                    <div justify="center" gap="middle" wrap="wrap" style={{ marginBottom: '24px' }}>
                        <Button
                            type="primary"
                            danger // Màu đỏ cho nút ghi/đang ghi
                            onClick={handleRecord}
                            disabled={isRecording || isProcessing} // Vô hiệu hóa khi đang ghi hoặc đang xử lý dừng
                            loading={isRecording} // Hiển thị loading khi isRecording = true
                            // icon={<span role="img" aria-label="record" className="anticon anticon-audio">{isRecording ? ' M' : ' M'}</span>} // Icon micro (ví dụ)
                        >
                            {isRecording ? 'Đang Ghi...' : 'Bắt đầu Ghi'}
                        </Button>
                        <Button
                            onClick={handleStop}
                            disabled={!isRecording || isProcessing} // Chỉ bật khi đang ghi và không xử lý
                            loading={isProcessing} // Hiển thị loading khi đang xử lý file sau khi dừng
                        >
                            Dừng Ghi
                        </Button>
                        <Button
                            type="primary"
                            onClick={handlePlay}
                            disabled={!audioUrl || isRecording || isProcessing} // Chỉ bật khi có audio, không ghi, không xử lý
                            icon={<span role="img" aria-label="play" className="anticon anticon-play-circle">▶</span>} // Icon Play
                        >
                            Phát Lại
                        </Button>
                    </div>

                    {/* Audio Player (hiển thị khi có audioUrl) */}
                    {audioUrl && (
                         <audio
                            ref={audioPlayerRef}
                            src={audioUrl}
                            controls
                            style={{ width: '100%', marginTop: '16px' }}
                            // Không nên tự động phát vì chính sách trình duyệt
                            // onLoadedData={() => console.log("Audio data loaded.")} // Debug
                        ></audio>
                    )}
                </Card>
            </Content>
        </Layout>
    );
};

export default TestMic;